import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import DropdownMenu from '../ui/DropdownMenu';
import { isFieldTech as isFieldTechRule } from '../../utils/isFieldTech';
import { useJobCompletion } from './JobCompletionDialog';
import { isJobCompleted } from '../../utils/jobCompletion';
import { Button } from '../ui/Button';
import StatusPill, { humanizeEnum } from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { useAuth } from '../../contexts/AuthContext';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { SkeletonTable } from '../ui/Skeleton';
import { Checkbox } from '../ui/Checkbox';
import PageHeader from '../ui/PageHeader';
import ListShell from '../ui/ListShell';
import { Alert } from '../ui/Alert';
import BulkActionBar from '../ui/BulkActionBar';
import EmptyState from '../ui/EmptyState';
import { SalespeopleChips } from '../ui/SalespeopleChips';
import { normaliseSalespeople } from '../../utils/normaliseSalespeople';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import SelectMenu from '../ui/SelectMenu';
import { jobAPI } from '../../services/api';
import { deriveJobStatus } from '../../utils/jobStatus';
import { formatJobNumber } from '../../utils/displayId';
import { formatCurrency } from '../../utils/formatCurrency';
import { getJobContractTotal } from '../../utils/jobContractTotal';
import { useSettingsContext } from '../../hooks/useSettings';
import useSettingsList from '../../hooks/useSettingsList';
import useSalespersonOptions from '../../hooks/useSalespersonOptions';
import SharedWorkOrderAccordion from '../shared/WorkOrderAccordion';
import { dealerToday } from '../../utils/dealerDate';

// ─── Status Filters ─────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { value: 'all', label: 'All Jobs' },
  { value: 'not_scheduled', label: 'Not Scheduled' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'partially_scheduled', label: 'Partially Scheduled' },
  { value: 'completed', label: 'Completed' },
];

// ─── Empty State ─────────────────────────────────────────────────────────────

// PT-17 (2026-07-24): the local `JobsEmptyState` component was deleted in favor
// of the shared `EmptyState` atom (src/components/ui/EmptyState.js). The
// two-CTA action slot is passed as a Fragment at the call site.

// ─── Reassign Picker Dialog (PT-17 17.9 + 17.10) ──────────────────────────────

/**
 * Small in-file picker used by the bulk-action bar. `mode='salesperson'` sources
 * options from `useSalespersonOptions` (user_role.v1 seed + can_sell filter);
 * `mode='crew'` sources from `useSettingsList('crews')`. Rendered as a shared
 * Dialog with a SelectMenu inside — no raw <select>, no hardcoded options list.
 * (Rule 10 — Seed/Settings first.)
 */
function ReassignPickerDialog({
  mode,
  value,
  onValueChange,
  busy,
  onClose,
  onSubmit,
  selectedCount,
  salespersonOptions,
  crewOptions,
}) {
  const isSalesperson = mode === 'salesperson';
  const isCrew = mode === 'crew';
  const open = isSalesperson || isCrew;

  const title = isSalesperson ? 'Reassign salesperson' : isCrew ? 'Reassign crew' : '';
  const label = isSalesperson ? 'New salesperson' : 'New crew';
  const options = isSalesperson ? salespersonOptions : isCrew ? crewOptions : [];

  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} size="sm">
      <DialogHeader onClose={busy ? undefined : onClose}>{title}</DialogHeader>
      <DialogBody>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
          Applies to {selectedCount} selected job{selectedCount === 1 ? '' : 's'}.
        </p>
        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
          {label}
        </label>
        <SelectMenu
          value={value}
          onChange={onValueChange}
          options={options}
          placeholder={options.length === 0 ? 'No options available' : `Select — ${options.length} available`}
          disabled={busy || options.length === 0}
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={busy || !value}>
          {busy ? 'Updating…' : `Reassign ${selectedCount}`}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// IDI-478: the job's contract total — totalAmount, else subtotal + taxAmount,
// else lockedTotalCents; "—" when none (never a guessed $0).
function JobTotalCell({ job }) {
  const money = getJobContractTotal(job);
  return (
    <TableCell align="right" className="tabular-nums" data-testid="job-total-cell">
      {money ? formatCurrency(money.totalCents) : '—'}
    </TableCell>
  );
}

// GP-07 / PR-7: extracted to shared/WorkOrderAccordion. This thin wrapper
// adds the TableRow/TableCell colSpan envelope for the Jobs landing table.

function WorkOrderAccordion({ workOrders, jobId, crews }) {
  const navigate = useNavigate();
  return (
    <TableRow>
      {/* IDI-182 v2 (2026-09-07): colSpan updated 7 → 8 for the new
          Job Type column. This envelope only renders for full-view users
          (the expander lives inside the !isFieldTech gate at :567); field
          techs never see this row so their column count doesn't apply
          here. Full-view column count: checkbox + Project + Customer +
          Salesperson + Job Type + Status + Date (Won) + Actions = 8.
          IDI-478: + Total = 9. */}
      <TableCell colSpan={9} className="p-0 bg-gray-50/50">
        <div className="pl-12 py-2">
          <SharedWorkOrderAccordion
            workOrders={workOrders}
            jobId={jobId}
            crews={crews}
            onCreateWO={() => navigate(`/jobs/${jobId}?tab=work-orders`)}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function JobsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEnumLabel } = useSettingsContext();
  // IDI-523 D3: the server's viewer rule (groups, else role).
  const isFieldTech = isFieldTechRule(user);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // PORTICA-56: true while more job batches are still streaming in behind an
  // already-rendered first page. See loadJobs below and
  // QuotesPage.js's identical pattern (IDI-56) for the full rationale.
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Iter-18: honor ?status=xxx URL param (e.g. dashboard KPI 'Jobs Not Scheduled' → /jobs?status=not_scheduled).
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status');
    return s || 'all';
  });
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  // IDI-103 (2026-09-08): pagination state. Copied from CustomersPage.js
  // :60-61 verbatim (per operator ruling — no atom extraction; four
  // pages hand-roll this pattern and unifying them is a refactor hiding
  // in a fix). CustomersPage / QuotesPage / ReportDetail / ItemCatalogPage
  // are the peers; this brings JobsPage into the same shape.
  //
  // pageSize=25 matches CustomersPage. The 3 pagination traps operator
  // named this turn are all handled below:
  //   · Reset page→1 on filter/search change (useEffect at :~163).
  //   · WO accordion expander (:668-671) is keyed by job id in a Set;
  //     paging away and back re-renders the same job with expandedJobs
  //     unchanged, so its expanded state survives (verified in tests).
  //   · Filtered-empty (0 results after filter) vs paginated-overflow
  //     (page N of N-1) render distinct states — filtered-empty via
  //     the existing EmptyState atom at :540-560, overflow prevented
  //     by clamping page to totalPages when filter changes.
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => { loadJobs(); }, []);

  // IDI-103 (2026-09-08): reset page to 1 whenever the filter or search
  // changes. Trap #1 from operator: "a user on page 3 filters to 4
  // results and sees an empty table." This is the fix. No debouncer on
  // JobsPage (unlike CustomersPage :74-83) — filter/search are direct.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  // PORTICA-56: PROGRESSIVE loading, same pattern as QuotesPage.js (IDI-56).
  // The backend now supports opt-in cursor pagination (limit/cursor ->
  // {jobs, has_more, next_cursor}). Fetch the first batch, render
  // immediately, then keep fetching more batches in the background and
  // MERGE them into the same `jobs` state the chip-count predicate
  // (statusMatches, line ~402) already reads. Chip counts and the search/
  // status filter still operate client-side over whatever has loaded so
  // far -- nothing about statusMatches or the filter changed, so the
  // count === rendered-rows invariant holds by construction, exactly as
  // before. The only difference is `jobs` grows in batches instead of
  // arriving as one blocking response that used to take ~11s for 314 rows
  // and sat right at the Lambda timeout edge.
  const loadSeqRef = React.useRef(0);
  const JOBS_BATCH_SIZE = 500;

  const loadJobs = async () => {
    const mySeq = ++loadSeqRef.current;
    let first = true;
    try {
      setLoading(true);
      setBackgroundLoading(false);
      setJobs([]);

      let cursor;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (loadSeqRef.current !== mySeq) {
          // IDI-103: instrumentation — if this fires, something called loadJobs()
          // again during the cursor chase, invalidating this run.
          console.warn('[JobsPage] loadJobs seq guard fired: mySeq=%d, current=%d', mySeq, loadSeqRef.current);
          return;
        }
        const response = await jobAPI.getJobs({ limit: JOBS_BATCH_SIZE, cursor });
        const body = response?.data || response || {};
        const batch = Array.isArray(body) ? body : (body.jobs || []);
        if (loadSeqRef.current !== mySeq) {
          console.warn('[JobsPage] loadJobs seq guard fired after fetch: mySeq=%d, current=%d', mySeq, loadSeqRef.current);
          return;
        }

        const isFirstBatch = first;
        setJobs((prev) => (isFirstBatch ? batch : [...prev, ...batch]));
        if (isFirstBatch) {
          setLoading(false);
          first = false;
        }

        const hasMore = Array.isArray(body) ? false : !!body.has_more;
        cursor = Array.isArray(body) ? undefined : body.next_cursor;
        if (!hasMore || !cursor) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[JobsPage] pagination complete: hasMore=%s, cursor=%s, totalLoaded=%d', hasMore, cursor, batch.length);
          }
          break;
        }
        setBackgroundLoading(true);
      }
    } catch (err) {
      if (loadSeqRef.current !== mySeq) return;
      if (first) {
        setError('Unable to load jobs.');
        setJobs([]);
      } else {
        setError('Some jobs may be missing — retry to load the rest.');
      }
      setLoading(false);
    } finally {
      if (loadSeqRef.current === mySeq) {
        setLoading(false);
        setBackgroundLoading(false);
      }
    }
  };

  // PT-27 27.6 (2026-07-24): status-only predicate extracted so chip counts stay
  // consistent with the filter itself. Per QuotesPage precedent (L225-234) the
  // chip counts do NOT apply the search term — they report total rows per status.
  //
  // IDI-143 (2026-09-02): this used to compare the RAW `j.status` string
  // against the filter value literally (`status === 'not_scheduled'`).
  // Real jobs never carry a literal status string of "not_scheduled" --
  // that bucket is DERIVED (normalizeJobStatus returns it as the `default`
  // case for anything unrecognized, e.g. a real job's actual stored status
  // of "active"/"completed"/etc. with no scheduling info). Confirmed live:
  // scanning bidit-dev-jobs shows only 'active'/'completed' as real stored
  // values, never the literal string 'not_scheduled'. So
  // /jobs?status=not_scheduled (the dashboard tile's click-through link)
  // always filtered to zero rows -- the exact "clicking it returns no
  // results" bug. Now routes through the SAME normalizeJobStatus the
  // Status pill and CSV export already use, so the filter, the pill, and
  // the dashboard tile's isJobBacklog predicate all agree by construction.
  const statusMatches = (j, filterValue) => {
    if (filterValue === 'all') return true;
    // IDI-139 §2 (2026-09-03, Adrian's ruling): route through deriveJobStatus
    // instead of the stored job.status. All 250 records carry 'active';
    // deriveJobStatus reads workOrders[].startAt instead. tile === table
    // === filter chips by construction — see jobStatus.js.
    const normalized = deriveJobStatus(j);
    if (filterValue === 'not_scheduled') return normalized === 'not_scheduled';
    if (filterValue === 'partially_scheduled') return normalized === 'partially_scheduled';
    if (filterValue === 'scheduled') return normalized === 'scheduled';
    if (filterValue === 'completed') return normalized === 'completed';
    return normalized === filterValue;
  };

  const jobsList = Array.isArray(jobs) ? jobs : [];

  const filtered = jobsList.filter(j => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
      (j.title || '').toLowerCase().includes(term) ||
      (j.customerName || j.customer_name || '').toLowerCase().includes(term) ||
      (formatJobNumber(j) || j.id || '').toLowerCase().includes(term) ||
      (j.salesperson || '').toLowerCase().includes(term)
    );
    return matchesSearch && statusMatches(j, statusFilter);
  });

  // IDI-103: derive pagination AFTER filter, so the count matches what
  // the user is looking at. `totalPages` is 1 when there are zero
  // filtered rows (rather than 0) so the "Showing 0 of 0" line has a
  // sensible page-count reference; the render below gates the whole
  // pagination block on `totalPages > 1` anyway (Trap #3 — no controls
  // on a single-page or empty result set).
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // IDI-479: header click-to-sort over ALL filtered rows, before the slice;
  // a sort change resets to page 1 (same pattern as the IDI-103 filter reset).
  // IDI-478's Total column sorts by contract-total cents.
  const jobSortColumns = [
    { key: 'project', type: 'text', value: (j) => j.title || formatJobNumber(j) },
    { key: 'customer', type: 'text', value: (j) => j.customerName || j.customer_name || null },
    { key: 'salesperson', type: 'text', value: (j) => normaliseSalespeople(j)[0]?.name || null },
    { key: 'jobType', type: 'text', value: (j) => j.jobType || j.job_type || getEnumLabel('job_types', j.jobTypeId || j.job_type_id) || null },
    { key: 'status', type: 'text', value: (j) => humanizeEnum(deriveJobStatus(j)) || null },
    { key: 'date', type: 'date', value: (j) => j.createdAt || j.created_at || null },
    { key: 'total', type: 'money', value: (j) => getJobContractTotal(j)?.totalCents ?? null },
  ];
  const { sortedRows: sortedJobs, getSortProps } = useTableSort(filtered, jobSortColumns, {
    onSortChange: () => setPage(1),
  });
  const paginatedJobs = sortedJobs.slice((page - 1) * pageSize, page * pageSize);

  const toggleJobSelection = (jobId, e) => {
    e.stopPropagation();
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedJobs.size === filtered.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(filtered.map(j => j.id)));
    }
  };

  const toggleExpand = (jobId, e) => {
    e.stopPropagation();
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  // ── PT-17 17.8/17.9/17.10: Bulk actions (real handlers) ─────────────────────
  //
  // Operator ruling 2026-07-24: no `jobAPI.bulkUpdate` endpoint exists; PT-17 ships
  // as N sequential `jobAPI.updateJob` calls. Correct semantics, no new endpoint.
  // Errors surface as "N of M failed" (never a silent drop). Salesperson list sourced
  // from useSalespersonOptions (user_role.v1 seed + can_sell filter). Crew list from
  // useSettingsList('crews'). Rule 10 (Seed/Settings first) satisfied end-to-end.

  const [reassignMode, setReassignMode] = useState(null); // 'salesperson' | 'crew' | null
  const [reassignValue, setReassignValue] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState(null);
  const { requestComplete, dialog: completionDialog } = useJobCompletion();

  const salespersonOptionsQuery = useSalespersonOptions();
  const crewsQuery = useSettingsList('crews');

  const runBulkUpdate = async (patch, successCopy) => {
    const ids = [...selectedJobs];
    if (ids.length === 0) return;
    setError(null);
    setBulkSuccess(null);
    setReassignBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => jobAPI.updateJob(id, patch))
    );
    setReassignBusy(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      setError(`${failed} of ${ids.length} jobs failed to update. Refresh and try again.`);
    } else {
      setBulkSuccess(successCopy || `${ids.length} job${ids.length === 1 ? '' : 's'} updated.`);
    }
    await loadJobs();
    setSelectedJobs(new Set());
    setReassignMode(null);
    setReassignValue('');
  };

  const handleBulkAction = (action) => {
    if (action === 'complete') {
      // IDI-246: through the same confirm as the Job page; each job goes
      // through complete_job (closes its open work orders, writes the Notes
      // entry). Already-completed jobs are left out (a repeat would 409).
      const ids = new Set(selectedJobs);
      const picked = jobs.filter((j) => ids.has(j.id) && !isJobCompleted(j));
      if (picked.length === 0) return;
      (async () => {
        const res = await requestComplete(picked);
        if (!res) return;
        setError(null);
        setBulkSuccess(null);
        if (res.failed.length > 0) {
          setError(`${res.failed.length} of ${picked.length} jobs failed to update. Refresh and try again.`);
        } else {
          setBulkSuccess(`${picked.length} job${picked.length === 1 ? '' : 's'} marked complete.`);
        }
        await loadJobs();
        setSelectedJobs(new Set());
      })();
      return;
    }
    if (action === 'reassign_salesperson') {
      setReassignMode('salesperson');
      setReassignValue('');
      return;
    }
    if (action === 'reassign_crew') {
      setReassignMode('crew');
      setReassignValue('');
      return;
    }
    if (action === 'export') {
      // PT-17 17.11 (patched 2026-07-24): client-side CSV export of the selected
      // jobs — no backend, no new endpoint. Columns mirror the visible table:
      // Job #, Project Name, Salesperson, Job Type, Status, Date (won).
      const ids = new Set(selectedJobs);
      const rows = jobs.filter((j) => ids.has(j.id));
      if (rows.length === 0) return;
      const escape = (v) => {
        const s = v == null ? '' : String(v);
        // RFC 4180: fields containing "," or `"` or newline get quoted; internal `"` doubled.
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = ['Job #', 'Project Name', 'Salesperson', 'Job Type', 'Status', 'Date Won'];
      const lines = [
        header.join(','),
        ...rows.map((j) => [
          formatJobNumber(j),
          j.title || j.project_name || j.projectName || '',
          j.salesperson || (Array.isArray(j.salespeople) ? j.salespeople.join('; ') : '') || '',
          j.jobType || j.job_type || getEnumLabel('job_types', j.jobTypeId || j.job_type_id) || '',
          // IDI-139 §2 (2026-09-03): CSV column mirrors the visible column;
          // both derive from workOrders[].startAt via deriveJobStatus.
          deriveJobStatus(j) || '',
          // IDI-139 §1 (2026-09-02): the writer never emits any of wonDate/won_date/
          // date_won/dateWon/wonAt on a job record. Verified at cmd/quotes/main.go
          // convertToJob (~:1760) — the only writer of new jobs emits `createdAt`
          // (=conversion time), plus copies of quote fields (none of them a "won"
          // timestamp). So the CSV column "Date Won" always exported empty. Using
          // createdAt to match what the writer actually emits; semantically this
          // reads "date job was created via conversion" which is the same event
          // as "date won" for any job that reached this list.
          // ⚠️ column header still reads "DATE (WON)" — see PR body.
          j.createdAt || j.created_at || '',
        ].map(escape).join(',')),
      ];
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs_${dealerToday()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setBulkSuccess(`Exported ${rows.length} job${rows.length === 1 ? '' : 's'} to CSV.`);
      setSelectedJobs(new Set());
      return;
    }
  };

  const submitReassign = () => {
    if (!reassignValue || reassignBusy) return;
    if (reassignMode === 'salesperson') {
      runBulkUpdate(
        { salesperson_id: reassignValue },
        `Reassigned ${selectedJobs.size} job${selectedJobs.size === 1 ? '' : 's'} to a new salesperson.`
      );
    } else if (reassignMode === 'crew') {
      runBulkUpdate(
        { crew_id: reassignValue },
        `Reassigned ${selectedJobs.size} job${selectedJobs.size === 1 ? '' : 's'} to a new crew.`
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '—';
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch {
      return '—';
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    // IDI-273: cols must match the rendered table's colSpan, which is
    // role-aware (isFieldTech ? 5 : 8 — see :594). This was hardcoded to 7
    // and never updated when IDI-182 v2 added the Job Type column.
    return <SkeletonTable rows={5} cols={isFieldTech ? 5 : 9} />;
  }

  // Empty state — no jobs at all
  if (!loading && jobs.length === 0) {
    return (
      <div>
        <PageHeader title="Jobs" actions={null} />
        <EmptyState
          mode="first-run"
          icon={ClipboardDocumentListIcon}
          title="No jobs yet"
          description="Jobs are created when a signed quote is converted. Start a new quote, or head to existing Quotes to convert a signed one."
          action={
            <div className="flex items-center justify-center gap-3">
              {/* IDI-523: no Create for a field tech. */}
              {!isFieldTech && (
                <Button onClick={() => navigate('/quotes/add')}>
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  Create a Quote
                </Button>
              )}
              <Button variant="secondary" onClick={() => navigate('/quotes')}>
                Go to Quotes
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="px-6">
      {/* Page Header */}
      <PageHeader
        title="Jobs"
        subtitle={isFieldTech ? 'Field Tech View' : undefined}
        actions={isFieldTech ? null : (
          <DropdownMenu
            ariaLabel="Create"
            trigger={
              <Button variant="primary">
                <PlusIcon className="h-4 w-4 mr-1" />
                Create
                <ChevronDownIcon className="h-4 w-4 ml-1" />
              </Button>
            }
            items={[
              { label: 'Create a Quote', onClick: () => navigate('/quotes/add') },
            ]}
          />
        )}
      />

      {/* Search + Filters via ListShell */}
      <ListShell
        search={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search jobs..."
        chips={STATUS_FILTERS.map(f => ({
          key: f.value,
          label: f.label,
          active: statusFilter === f.value,
          // PT-27 27.6 (2026-07-24): per-chip counts via FilterChip's existing `count` prop.
          // Mirrors QuotesPage.js:225-234 — counts do NOT apply the search term.
          count: jobsList.filter((j) => statusMatches(j, f.value)).length,
          onClick: () => setStatusFilter(f.value),
        }))}
      >

      {/* Error + success (PT-17 17.8/17.9/17.10 bulk-action feedback). */}
      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
      {bulkSuccess && <Alert variant="success" className="mb-4" onClose={() => setBulkSuccess(null)}>{bulkSuccess}</Alert>}

      {/* PORTICA-56: informational only — more job batches are still loading
          in the background behind the rows already shown below. */}
      {backgroundLoading && (
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
          Loading more jobs…
        </p>
      )}
      {/* Jobs Table */}
      <Table>
        <TableHead>
          <TableRow>
            {!isFieldTech && (
              <TableHeader className="w-8">
                <Checkbox
                  checked={selectedJobs.size === filtered.length && filtered.length > 0}
                  onChange={() => toggleSelectAll()}
                  aria-label="Select all jobs"
                />
              </TableHeader>
            )}
            <TableHeader {...getSortProps('project')}>Project</TableHeader>
            <TableHeader {...getSortProps('customer')}>Customer</TableHeader>
            {!isFieldTech && <TableHeader {...getSortProps('salesperson')}>Salesperson</TableHeader>}
            {/* IDI-182 v2 (2026-09-07): Job Type column. Visible to field techs
                too — it's a work descriptor (residential/commercial/etc.), not
                a person/assignment field. See row cell below for reader
                rationale; the reader is reused verbatim from the CSV export
                at :374 per operator's "do not write a new one" ruling. */}
            <TableHeader {...getSortProps('jobType')}>Job Type</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            <TableHeader {...getSortProps('date')}>Date (Won)</TableHeader>
            {/* IDI-478: contract total (same resolver as the Job Details card).
                Hidden from field techs (IDI-523). Sorts by cents (IDI-479). */}
            {!isFieldTech && <TableHeader align="right" {...getSortProps('total')}>Total</TableHeader>}
            {!isFieldTech && <TableHeader className="w-10" aria-label="Actions" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              {/* PT-17 17.2: filtered-empty branch — shared EmptyState atom in filtered
                  mode; "Clear filters" affordance resets search + status. No create CTA
                  in this branch per EmptyState atom's mode='filtered' contract. */}
              {/* IDI-182 v2 (2026-09-07): colSpan updated to account for
                  the new Job Type column. Field-tech visible columns:
                  Project + Customer + Job Type + Status + Date (Won) = 5.
                  Full-view visible columns: checkbox + Project + Customer
                  + Salesperson + Job Type + Status + Date (Won) +
                  Actions = 8. */}
              <TableCell colSpan={isFieldTech ? 5 : 9} className="py-4">
                <EmptyState
                  mode="filtered"
                  icon={ClipboardDocumentListIcon}
                  title="No jobs match your filters"
                  description="Try clearing the status filter or search term."
                  onClearFilters={() => {
                    setStatusFilter('all');
                    setSearchTerm('');
                    setPage(1);
                  }}
                />
              </TableCell>
            </TableRow>
          ) : (
            paginatedJobs.map((job) => {
              const isExpanded = expandedJobs.has(job.id);
              const isSelected = selectedJobs.has(job.id);
              const workOrders = job.work_orders || job.workOrders || [];

              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    // PT-17 17.6: selected-row highlight via new semantic token
                    // (--color-bg-selected in tokens.css); replaces hardcoded bg-red-50.
                    className={isSelected ? 'bg-[color:var(--color-bg-selected)]' : ''}
                  >
                    {!isFieldTech && (
                      <TableCell onClick={(e) => e.stopPropagation()} className="w-8">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={isSelected}
                            stopPropagation
                            onChange={(_v, e) => toggleJobSelection(job.id, e)}
                            aria-label="Select job"
                          />
                          {workOrders.length > 0 && (
                            <button
                              onClick={(e) => toggleExpand(job.id, e)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {isExpanded
                                ? <ChevronDownIcon className="h-4 w-4" />
                                : <ChevronRightIcon className="h-4 w-4" />
                              }
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{job.title || formatJobNumber(job)}</p>
                        <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{formatJobNumber(job)}</p>
                      </div>
                    </TableCell>
                    <TableCell>{job.customerName || job.customer_name || "—"}</TableCell>
                    {!isFieldTech && (
                      <TableCell>
                        {(() => {
                          const norm = normaliseSalespeople(job);
                          if (norm.length === 0) return "—";
                          return <SalespeopleChips salespeople={norm} interactive={false} />;
                        })()}
                      </TableCell>
                    )}
                    <TableCell>
                      {/* IDI-263 (2026-09-08): singular `'job_type'` at this site
                          + :416 (CSV) + :670 (inline comment reference) was
                          the outlier flagged as "observation, not defect" in
                          the prior IDI-182 v2 comment. Corrected to `'job_types'`
                          to match the convention everywhere else in the codebase
                          (JobForm.js:34,148 · ProjectDetailsCard.js:96 ·
                          useSettings.test.js:398,413). Wire data on tenant 270
                          populates `jobType` on 200/200 rows so the fallback is
                          effectively dead there — but the CSV export at :416
                          does hit it, and the CSV column must agree with the
                          on-screen Type column. All three call sites in this
                          file were fixed in one commit. */}
                      {job.jobType || job.job_type || getEnumLabel('job_types', job.jobTypeId || job.job_type_id) || "—"}
                    </TableCell>
                    <TableCell>
                      {/* IDI-139 §2 (2026-09-03, Adrian's ruling): derive from
                          workOrders[].startAt, not stored job.status. */}
                      <StatusPill status={deriveJobStatus(job)} />
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDate(job.createdAt || job.created_at) || "—"}
                    </TableCell>
                    {!isFieldTech && (
                      <JobTotalCell job={job} />
                    )}
                    {!isFieldTech && (
                      <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                        <DropdownMenu
                          ariaLabel="Job actions"
                          stopPropagation
                          items={[
                            { label: 'View', onClick: () => navigate(`/jobs/${job.id}`) },
                            { label: 'Work Orders', onClick: () => navigate(`/jobs/${job.id}?tab=work-orders`) },
                            { label: 'Invoices', onClick: () => navigate(`/jobs/${job.id}?tab=invoices`) },
                          ]}
                        />
                      </TableCell>
                    )}
                  </TableRow>

                  {/* WO Accordion */}
                  {isExpanded && (
                    <WorkOrderAccordion workOrders={workOrders} jobId={job.id} crews={crewsQuery?.items} />
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* IDI-103 (2026-09-08): pagination controls. Copied from
          CustomersPage.js :449-471 verbatim, with jobs-list wording.
          Gated on totalPages > 1 (Trap #3): on a single-page or empty
          result set, no controls render — the visible count line is
          also omitted to avoid a "Showing 0 of 0 · Page 1 of 1" panel
          that would compete with the EmptyState atom above. */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      </ListShell>

      {/* Bulk Action Bar — shared component (RF-17-11) */}
      <BulkActionBar
        // PT-17 bug-fix: BulkActionBar's prop is `count`, not `selectedCount`. Before
        // this fix the bar silently didn't render (all 4 bulk actions were unreachable).
        count={selectedJobs.size}
        onClear={() => setSelectedJobs(new Set())}
        actions={[
          { label: 'Complete Job', onClick: () => handleBulkAction('complete') },
          { label: 'Reassign Salesperson', onClick: () => handleBulkAction('reassign_salesperson') },
          { label: 'Reassign Crew', onClick: () => handleBulkAction('reassign_crew') },
          { label: 'Export', onClick: () => handleBulkAction('export') },
        ]}
      />
      {completionDialog}

      {/* PT-17 17.9/17.10: Reassign picker dialog. Sourced from Seed/Settings
          (useSalespersonOptions + useSettingsList('crews')). Submits N sequential
          jobAPI.updateJob calls via runBulkUpdate. */}
      <ReassignPickerDialog
        mode={reassignMode}
        value={reassignValue}
        onValueChange={setReassignValue}
        busy={reassignBusy}
        onClose={() => { setReassignMode(null); setReassignValue(''); }}
        onSubmit={submitReassign}
        selectedCount={selectedJobs.size}
        salespersonOptions={salespersonOptionsQuery?.options || []}
        crewOptions={(crewsQuery?.items || []).map((c) => ({
          value: c.id || c.value,
          label: c.name || c.label || c.id,
        }))}
      />
    </div>
  );
}

// Named export for testing (pattern: CustomerDetail.js:838)
export { WorkOrderAccordion };
